import React, {
  ReactElement,
  useCallback,
  useEffect, useMemo,
  useRef,
  useState,
} from 'react';
import ReactDOM from 'react-dom';
import {
  InstantSearch,
  HitsPerPage,
  Panel,
  Configure,
  InfiniteHits,
  connectRefinementList,
} from 'react-instantsearch-dom';
import { NoResults } from '../../search/widgets';
import {
  ALGOLIA_BREEDER_INDEX,
  ALGOLIA_SORT_BY_LOCATION_IF_SET,
  breederSearchRefinements,
  listingAttributesToHighlightBreederCard,
  listingAttributesToRetrieveBreederCard,
} from '../../../utils/constants';
import BreederCard from '../card/breeder-card';
import getSelectDropdown from '../../search/widgets/filters/select-dropdown';
import useRequestLocation from '../../../hooks/useRequestLocation';

import useURLSync from '../../../hooks/useURLSync';
import fetchSearchParams from '../../../utils/fetchSearchParams';
import { LocationParams, SearchState } from '../../search/admin-dashboard-tables/interfaces';
import CustomSortBy from '../../search/widgets/sorts/CustomSortBy';
import DebouncedSearchBox from '../../search/widgets/DebouncedSearchBox';
import { useAlgoliaSearchClientLite } from '../../../utils/hooks/useAlgoliaSearchClient';
import getInstantSelectDropdown from '../../search/widgets/filters/InstantSelectDropdown';
import useApplicableFilters from '../../../hooks/useApplicableFilters';
import CustomCurrentBreedRefinement from '../../search/widgets/CustomCurrentRefinements/CustomCurrentBreedRefinement';
import CustomCurrentRefinements from '../../search/widgets/CustomCurrentRefinements/CustomCurrentRefinements';
import ClearRefinements from '../../search/widgets/ClearRefinements';
import getEmptyFilter from '../../search/widgets/filters/empty-filter';

interface SortParams extends LocationParams {
  sortBy?: string,
}

const Hit: React.FC = ({ hit }: any): ReactElement => {
  return (
    <div className="react-breeder-card">
      <BreederCard breeder={hit} key={hit.objectID} />
    </div>
  );
};

const CustomInstantBreedFilter = connectRefinementList(getInstantSelectDropdown(true, 'Breed', [], '', true));
const CustomStateFilter = connectRefinementList(getSelectDropdown(true, 'State', [], '', true));
const EmptyFilter = connectRefinementList(getEmptyFilter());

const getInitialRefinementsForSearch = (searchState: SearchState, instantRefinements: string[]) => {
  const { refinementList = {} } = searchState;
  const initialRefinementsForSearch = {};
  breederSearchRefinements
    .forEach((attribute) => {
      // Don't add instantRefinements
      if (!instantRefinements.includes(attribute)) {
        initialRefinementsForSearch[attribute] = [];
      }
    });
  Object.keys(refinementList).forEach((attribute) => {
    // Don't add instantRefinements
    if (!instantRefinements.includes(attribute)) {
      initialRefinementsForSearch[attribute] = refinementList[attribute];
    }
  });
  return initialRefinementsForSearch;
};

const BreederSearch = (props: any) => {
  const containerRef: any = useRef(null);

  const {
    filterByState,
    filterByBreed,
  } = props;

  const {
    createURL,
    resetPagination,
    searchState,
    setSearchState,
    user,
  } = useURLSync(breederSearchRefinements);

  const { searchClient } = useAlgoliaSearchClientLite(user?.public_key);

  const getInitialSort = () => {
    const {
      lat,
      lng,
      sortBy,
    }: SortParams = fetchSearchParams();
    if (
      (lat && lng)
      || (
        // If location is set and no sort is specified, we can default to location
        ALGOLIA_SORT_BY_LOCATION_IF_SET
        && localStorage.getItem('latitude')
        && localStorage.getItem('longitude')
        && !sortBy
      )
    ) {
      return 'location';
    }
    if (sortBy) {
      return sortBy;
    }
    return ALGOLIA_BREEDER_INDEX!;
  };

  const getInitialIndexName = (currentSort: string) => {
    const { sortBy }: SortParams = fetchSearchParams();
    if (sortBy) {
      return sortBy;
    }
    if (currentSort) {
      if (currentSort === 'location') {
        return ALGOLIA_BREEDER_INDEX!;
      }
      return currentSort;
    }
    return ALGOLIA_BREEDER_INDEX!;
  };

  const [query, setQuery] = useState(searchState?.query || '');
  // This is the value used to track the form state (not the indexName passed to Algolia)
  const [currentSort, setCurrentSort] = useState(getInitialSort());
  // This is the actual indexName passed to Algolia
  const [indexName, setIndexName] = useState(getInitialIndexName(currentSort));
  const [defaultSortRefinement] = useState<string>(ALGOLIA_BREEDER_INDEX!);

  const shouldSortByLocationOnLoad = currentSort === 'location';

  const {
    searchLocation,
    useSearchByLocation,
    toggleSearchByLocation,
  } = useRequestLocation(undefined, shouldSortByLocationOnLoad);

  const instantRefinements = [
    'breed',
    ...filterByState ? ['state'] : [],
  ];

  const initialRefinementsRef: React.MutableRefObject<
    null | {[key: string]: string[]}
  > = useRef(null);
  // This pattern prevents `getInitialRefinementsForSearch` from running on every render;
  // cf. https://react.dev/reference/react/useRef#avoiding-recreating-the-ref-contents
  if (initialRefinementsRef.current === null) {
    initialRefinementsRef.current = getInitialRefinementsForSearch(searchState, instantRefinements);
  }
  const stateRefinement = filterByState ? { defaultRefinement: [filterByState] } : {};
  const breedRefinement = filterByBreed ? { defaultRefinement: [filterByBreed] } : {};

  const ignoredRefinements = [
    ...filterByState ? ['state'] : [],
    ...filterByBreed ? ['breed'] : [],
  ];

  useEffect(() => {
    const newIndexName = currentSort === 'location'
      ? ALGOLIA_BREEDER_INDEX
      : currentSort;
    if (newIndexName) {
      setIndexName(newIndexName);
      // Reset pagination whenever sort is changed
      resetPagination(newIndexName);
    }
  }, [currentSort]);

  const {
    isDirty,
    filtersListToApply,
    updatePendingRefinements,
    applyFilters,
    resetFilters,
    removeSelectedAttributeValue,
    clearQueuedOptionsFromPendingFilters,
  } = useApplicableFilters(
    initialRefinementsRef.current,
    [],
  );

  /**
   *  This method prevents the connected components and the internal search state setter
   *  from updating the refinements that are “applicable”/non-instant.
   */
  const customSearchStateChange = useCallback((newSearchState) => {
    setSearchState((currentSearchState) => {
      const updatedConnectedRefinementList = {};
      const { refinementList: newRefinementList = {} } = newSearchState;
      Object.keys(newRefinementList)
        .forEach((refinementKey) => {
          if (instantRefinements.includes(refinementKey)) {
            updatedConnectedRefinementList[refinementKey] = newRefinementList[refinementKey];
          }
        });
      return ({
        ...newSearchState,
        refinementList: {
          ...currentSearchState.refinementList,
          ...updatedConnectedRefinementList,
        },
      });
    });
  }, [setSearchState]);

  useEffect(() => {
    const updatedQueryString = searchState.query || '';
    if (updatedQueryString !== query) {
      setQuery(updatedQueryString);
      clearQueuedOptionsFromPendingFilters();
    }
  }, [query, searchState, clearQueuedOptionsFromPendingFilters]);

  const customSortByCallback = (value: string) => {
    if (value === 'location') {
      // Since sorting by location can alter available filter options,
      // we should reset queued filters here
      clearQueuedOptionsFromPendingFilters();
      toggleSearchByLocation(true);
    } else {
      toggleSearchByLocation(false);
    }
    setCurrentSort(value);
  };

  // If user turns on location, sort by location immediately
  useEffect(() => {
    if (
      ALGOLIA_SORT_BY_LOCATION_IF_SET
      && useSearchByLocation
      && currentSort !== 'location'
    ) {
      // Since sorting by location can alter available filter options,
      // we should reset queued filters here
      clearQueuedOptionsFromPendingFilters();
      setCurrentSort('location');
    }
  }, [useSearchByLocation]);

  /**
   * Update searchState when `filtersListToApply` is updated
   */
  useEffect(() => {
    const {
      refinementList: refinementListToApply,
    } = filtersListToApply;

    setSearchState((currentSearchState) => {
      const { refinementList: currentRefinementList = {} } = currentSearchState;
      const newRefinementList = { ...refinementListToApply };

      instantRefinements.forEach((instantRefinement) => {
        if (currentRefinementList[instantRefinement]) {
          newRefinementList[instantRefinement] = currentRefinementList[instantRefinement];
        }
      });

      return {
        ...currentSearchState,
        page: 1, // Page must be updated manually, since we no longer use `refine`
        refinementList: newRefinementList,
      };
    });
  }, [filtersListToApply]);

  /**
   * Callback method to use whenever filters are reset.
   * This method resets the applicable filters to an initial,
   * default state.
   */
  const onReset = useCallback(() => {
    const clearedRefinements = {
      numericFilters: [],
      refinementList: {},
    };
    breederSearchRefinements
      .filter((attribute) => {
        return !ignoredRefinements.includes(attribute) && !instantRefinements.includes(attribute);
      })
      .filter((attribute) => !instantRefinements.includes(attribute))
      .forEach((attribute) => {
        clearedRefinements.refinementList[attribute] = [];
      });

    resetFilters(clearedRefinements);
  }, [
    resetFilters,
  ]);

  const customSortByOptions = useMemo(() => {
    const options = [
      {
        label: 'Sort by name (a-z)',
        value: defaultSortRefinement,
      },
    ];

    if (searchLocation) {
      options.push(
        {
          label: 'Sort by location (near-far)',
          value: 'location',
        },
      );
    }

    return [
      ...options,
      {
        label: 'Sort by name (z-a)',
        value: `${ALGOLIA_BREEDER_INDEX}_name_za`,
      },
    ];
  }, [defaultSortRefinement, searchLocation]);

  return (
    <InstantSearch
      searchClient={searchClient}
      indexName={indexName}
      searchState={searchState}
      createURL={createURL}
      onSearchStateChange={customSearchStateChange}
    >
      <Configure
        filters="publication_status:published AND isSearchableSeller:true"
        attributesToSnippet={['description:2']}
        snippetEllipsisText="…"
        removeWordsIfNoResults="allOptional"
        getRankingInfo={true}
        attributesToRetrieve={listingAttributesToRetrieveBreederCard}
        attributesToHighlight={listingAttributesToHighlightBreederCard}
        aroundRadius="all"
        aroundLatLng={useSearchByLocation ? searchLocation : ''}
      />
      <section className="results-container py-5">
        <div className="container-filters">
          <div className="my-3">
            <DebouncedSearchBox
              placeholder="Search Breeders..."
              useNativeStyles
            />
          </div>
          <div className="d-flex flex-column flex-lg-row justify-content-between">
            <div className="d-flex flex-wrap mb-2 mb-lg-0 breeder-filters">
              <Panel>
                {filterByBreed ? (
                  <EmptyFilter
                    attribute="breed"
                    {...breedRefinement}
                  />
                ) : (
                  <CustomInstantBreedFilter
                    limit={1000}
                    onRefineCallback={clearQueuedOptionsFromPendingFilters}
                    attribute="breed"
                  />
                )}
              </Panel>
              <Panel>
                {filterByState ? (
                  <EmptyFilter
                    attribute="state"
                    {...stateRefinement}
                  />
                ) : (
                  <CustomStateFilter
                    limit={100}
                    attribute="state"
                    updatePendingRefinements={updatePendingRefinements}
                    applyFilters={applyFilters}
                    isDirty={isDirty}
                  />
                )}
              </Panel>
              {!filterByState && (
                <div className="ais-Panel d-flex justify-content-center align-content-center">
                  <button
                    className="btn btn-primary rounded-lg font-weight-semibold px-5"
                    onClick={() => applyFilters()}
                    disabled={!isDirty}
                    type="button"
                  >
                    Apply
                  </button>
                </div>
              )}
              {(!filterByBreed || !filterByState) && (
                <div className="clear-filters" data-layout="desktop">
                  <ClearRefinements
                    onReset={onReset}
                    className="clear-filters-button"
                    label="Clear Filters"
                  />
                </div>
              )}
            </div>
            <div className="d-flex flex-column flex-md-row mb-2 mb-md-0">
              <CustomSortBy
                wrapperClass="container-option my-2 my-md-auto mx-0 mx-md-3"
                onChangeCallback={customSortByCallback}
                selected={currentSort}
                defaultRefinement={defaultSortRefinement}
                items={customSortByOptions}
              />
              <HitsPerPage
                className="container-option my-2 my-md-auto"
                items={[
                  {
                    label: 'Show: 15',
                    value: 15,
                  },
                  {
                    label: 'Show: 30',
                    value: 30,
                  },
                  {
                    label: 'Show: 60',
                    value: 60,
                  },
                  {
                    label: 'Show: 100',
                    value: 100,
                  },
                ]}
                defaultRefinement={100}
              />
            </div>
          </div>
        </div>
        <CustomCurrentRefinements
          currentlyAppliedAttributes={filtersListToApply.refinementList}
          onRemove={removeSelectedAttributeValue}
        >
          <CustomCurrentBreedRefinement
            attribute="breed"
            instantRefinements={instantRefinements}
            searchState={searchState}
            setSearchState={setSearchState}
            onRemoveCallback={clearQueuedOptionsFromPendingFilters}
          />
        </CustomCurrentRefinements>
        <main className="search-results-container" ref={containerRef}>
          <section className="container-results">
            <InfiniteHits hitComponent={Hit} />
            <NoResults />
          </section>
        </main>
      </section>
    </InstantSearch>
  );
};

export default BreederSearch;

const element = document.getElementById('react-breeder-search');
if (element) {
  const filterByState = element.getAttribute('data-state') ?? '';
  const filterByBreed = element.getAttribute('data-breed') ?? '';
  ReactDOM.render(
    <BreederSearch
      filterByState={filterByState}
      filterByBreed={filterByBreed}
    />,
    document.getElementById('react-breeder-search'),
  );
}
